import React from "react";
import Button from "components/foundation/Button";
import openPdfPreview, { getPdfPreviewUrl } from "utils/openPdfPreview";
import styled from "styled-components";
import { CopyIcon, DownloadIcon, EyeOpenIcon } from "assets/icons";
import Toast, { useToast } from "components/foundation/Toast";
import { preparePdfBlobForOutput } from "utils/pdf/preparePdfBlobForOutput";

interface DocumentDetailPdfButtonsProps
  extends React.HTMLAttributes<HTMLDivElement> {
  pdfUrl: string;
  pdfAvailable: boolean;
  basePdfUrl?: string;
  attachmentUrls?: string[];
  pdfTitle?: string;
}

const DocumentDetailPdfButtons = ({
  pdfUrl,
  pdfAvailable,
  basePdfUrl,
  attachmentUrls = [],
  pdfTitle,
  ...rest
}: DocumentDetailPdfButtonsProps) => {
  const outputBasePdfUrl = basePdfUrl || pdfUrl;

  // 미리보기
  /**
   * @description PDF 미리보기 버튼 이벤트 핸들러
   */
  const onClickOpenPDF = () => {
    openPdfPreview(outputBasePdfUrl, pdfTitle, attachmentUrls);
  };

  // 다운로드
  /**
   * @description PDF 다운로드 버튼 클릭 이벤트 핸들러
   */
  const onClickDownloadPDF = async () => {
    try {
      const pdfBlob = await preparePdfBlobForOutput({
        fileUrl: outputBasePdfUrl,
        attachmentUrls,
        title: pdfTitle,
      });
      const blobUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = pdfTitle || "download.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("PDF download failed:", error);
    }
  };

  // 복사
  const {
    open: openCopyToast,
    onOpenChange: onOpenChangeCopyToast,
    onOpenToast: onOpenCopyToast,
  } = useToast();

  /**
   * @description PDF 복사 버튼 클릭 이벤트 핸들러
   */
  const onClickCopyPDFUrl = () => {
    const previewUrl = getPdfPreviewUrl(
      outputBasePdfUrl,
      pdfTitle,
      attachmentUrls,
    );

    if (!previewUrl) {
      return;
    }

    navigator.clipboard.writeText(
      new URL(previewUrl, window.location.origin).toString(),
    );
    onOpenCopyToast();
  };

  return (
    <>
      <WrapStyle {...rest}>
        <HeaderActionsStyle>
          <Button
            variant="tertiary"
            size="large"
            onClick={onClickOpenPDF}
            leftIcon={<EyeOpenIcon />}
            disabled={!pdfUrl}
          >
            PDF 미리보기
          </Button>
          <Button
            variant="tertiary"
            size="large"
            onClick={onClickDownloadPDF}
            leftIcon={<DownloadIcon />}
            disabled={!pdfUrl}
          >
            PDF 다운로드
          </Button>
          <Button
            variant="tertiary"
            size="large"
            onClick={onClickCopyPDFUrl}
            leftIcon={<CopyIcon />}
            disabled={!pdfUrl}
          >
            PDF 링크복사
          </Button>
        </HeaderActionsStyle>

        {!pdfAvailable && (
          <WarningMessageStyle>
            문서 결재 이후 pdf 기능이 활성화됩니다.
          </WarningMessageStyle>
        )}
      </WrapStyle>

      <Toast.Message open={openCopyToast} onOpenChange={onOpenChangeCopyToast}>
        PDF 링크가 복사됐습니다.
      </Toast.Message>
    </>
  );
};
export default DocumentDetailPdfButtons;

/* 스타일 */
const WrapStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;

const WarningMessageStyle = styled.div`
  color: ${({ theme }) => theme.color.text.tertiery};
  ${({ theme }) => theme.media.small} {
    ${({ theme }) => theme.typography.body3.regular}
  }
`;

const HeaderActionsStyle = styled.div`
  display: flex;
  gap: 1rem;

  ${({ theme }) => theme.media.small} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;

    button {
      padding: 1.2rem 2rem;

      ${({ theme }) => theme.typography.body3.semibold}

      :nth-child(1) {
        grid-column: 1 / span 2;
      }
    }
  }
`;
